import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredCategoryArticles = articles.filter((a) => a.categorySlug === slug);
  const categoryArticles = filteredCategoryArticles.length ? filteredCategoryArticles : articles;

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://moonr.cn/' },
      { '@type': 'ListItem', position: 2, name: category.name },
    ],
  };

  return (
    <>
      <SEO
        title={`${category.name} | 51吃瓜黑料网 - ${category.description}`}
        description={`51吃瓜黑料网${category.name}频道 - ${category.description}。51吃瓜黑料网站🔥承接网曝门与在线爆料搜索需求，网红猛料实时追踪不停更。`}
        keywords={`51吃瓜黑料网, ${category.name}, 吃瓜, 黑料, 爆料, 网红`}
        ogTitle={`${category.name} | 51吃瓜黑料网`}
        ogDescription={category.description}
        ogImage="https://readdy.ai/api/search-image?query=Abstract%20modern%20entertainment%20news%20category%20header%20banner%2C%20warm%20coral%20and%20amber%20gradient%20tones%2C%20stylized%20geometric%20pattern%20layout%2C%20clean%20editorial%20aesthetic%20design%2C%20minimalist%20composition%20with%20subtle%20grid%20texture%2C%20no%20text%20or%20words%2C%20pure%20abstract%20visual%20for%20social%20sharing&width=1200&height=630&seq=og-category-wangbaomen&orientation=landscape"
        schema={breadcrumbSchema}
      />
      <Navbar />

      <main className="pt-16">
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="mb-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{category.name}</span>
            </nav>
          </div>

          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-50 flex items-center justify-center">
              <i className={`${category.icon} text-xl md:text-2xl text-primary-500`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">{category.name}</h1>
              <p className="mt-1 text-sm md:text-base text-foreground-500">{category.description}</p>
            </div>
          </div>
          <p className="text-xs text-foreground-400 mt-3">
            共 <strong className="text-foreground-700">{categoryArticles.length}</strong> 篇文章
          </p>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无相关文章</p>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="rounded-lg overflow-hidden bg-background-50 border border-background-200/70 group cursor-pointer transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-48 md:h-52 bg-background-100">
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                    </div>
                    {article.isHot && (
                      <span className="absolute top-3 left-3 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap">
                        热
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {(article.viewCount / 10000).toFixed(1)}万
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {article.commentCount}
                        </span>
                      </div>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="text-center">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">浏览更多分类</h2>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}